import axios from 'axios';
import { ApiClient } from './api-client';
import type {
  ApiResponse,
  Completeness,
  PortfolioImage,
  ProviderDetail,
  ProviderMine,
  ProviderServiceItem,
  ProviderStatus,
  ProviderSummary,
  WeeklyHours,
  WeeklyHoursDay,
} from '../types';

/**
 * Per-provider portfolio cap mirrored from {@code ProvidersService.MAX_PORTFOLIO_IMAGES}
 * on the backend. Frontend pages use this to disable "Add image" controls
 * once a provider has reached the limit; the backend rejects any extra
 * POST so a manual API call can't bypass the UX guard.
 */
export const MAX_PORTFOLIO_IMAGES = 20;

export interface ProviderSearchQuery {
  cityId?: string;
  /** Free-text city filter used when no {@code cityId} is available
   *  (cities picked from Geoapify / country-state-city). Case-insensitive
   *  substring match against the denormalised {@code city_name} column,
   *  with diacritics / ß flattened on both sides (see V44). */
  cityName?: string;
  categoryId?: string;
  /** ISO-3166-1 alpha-2 country code (e.g. "FR"). Scopes the entire
   *  result set to the visitor's chosen market so a French visitor never
   *  sees German providers and vice-versa. Backed by
   *  {@code providers.country_code}; omit it (or pass an empty string) to
   *  search across all markets. */
  countryCode?: string;
  /** Free-text fragment matched (case-insensitive) against the business name. */
  q?: string;
  /**
   * Coordinates of the searched city (V48 service coverage). The
   * country-state-city / Geoapify picker carries lat/lng for every city,
   * so the search bar forwards them here. When present on a city-scoped
   * search, opted-in providers whose base city is within their travel
   * radius are also returned, flagged {@code travelProvider}. Omit them
   * and the travel layer is simply off — local search is unaffected.
   */
  searchLat?: number;
  searchLng?: number;
  page?: number;
  size?: number;
}

/**
 * One row of {@link providersService.citiesForCategory} — a city where a
 * given category already has approved providers.
 */
export interface CategoryCityCoverage {
  city: string;
  /** Seeded {@code cities.slug}, or null when the matched providers only
   *  carry a denormalised free-text city name. */
  slug: string | null;
  /** ISO-3166-1 alpha-2 country code. */
  country: string;
  providerCount: number;
}

export interface BecomeProviderPayload {
  businessName: string;
  /** Legacy — optional now.  New code sends countryCode + cityName instead. */
  cityId?: string;
  /** Denormalised location from the country-state-city picker. */
  countryCode?: string;
  countryName?: string;
  stateCode?: string;
  stateName?: string;
  cityName?: string;
  latitude?: number;
  longitude?: number;
  bio?: string;
  whatsappNumber?: string;
  /** V48 service coverage — opt-in to out-of-city jobs + reach in km. */
  canTravel?: boolean;
  travelRadiusKm?: number;
  /**
   * Onboarding wizard sends `'DRAFT'` on the initial POST so the profile
   * is saved without being queued for moderation.  Omitted (or any other
   * value) keeps the legacy "submit immediately" behaviour.
   */
  mode?: 'DRAFT' | 'PUBLISH';
}

export interface UpdateProviderPayload {
  businessName?: string;
  /** Legacy — prefer countryCode + cityName below. */
  cityId?: string;
  /** Denormalised location fields from the country-state-city picker. */
  countryCode?: string;
  countryName?: string;
  stateCode?: string;
  stateName?: string;
  cityName?: string;
  latitude?: number;
  longitude?: number;
  bio?: string;
  whatsappNumber?: string;
  /**
   * Social-media URLs. Send "" to clear an existing value; omit (undefined)
   * to leave it unchanged. The backend stores blanks as NULL so the public
   * shop strip never renders an empty icon.
   */
  socialInstagram?: string;
  socialTiktok?: string;
  socialFacebook?: string;
  socialSnapchat?: string;
  socialYoutube?: string;
  socialTwitter?: string;
  /**
   * V48 service coverage. {@code canTravel} toggles out-of-city jobs;
   * {@code travelRadiusKm} (1–2000) is the reach from the base city.
   * Partial-update semantics — omit (undefined) to leave unchanged.
   */
  canTravel?: boolean;
  travelRadiusKm?: number;
}

/**
 * Provider-defined service: { categoryId, customName, ... }.  Providers
 * own their service list; the platform only owns the category taxonomy.
 *
 * `serviceCatalogId` remains in the type for backward-compatibility with
 * services attached via the old catalog flow.  New writes should always
 * use the (categoryId + customName) shape — the backend rejects requests
 * that supply both or neither.
 */
export interface AddProviderServicePayload {
  serviceCatalogId?: string;
  categoryId?: string;
  customName?: string;
  customPriceCents?: number;
  currency?: string;
  /**
   * "FIXED" → the price is exact, rendered as "80 €".
   * "STARTING_FROM" → baseline price, rendered as "From 80 €".
   * Omit when the service has no price.
   */
  priceType?: 'FIXED' | 'STARTING_FROM';
  durationMinutes?: number;
  description?: string;
}

/**
 * Partial update for an existing service row.  Category / catalog binding
 * are immutable — to "change" them the provider deletes and re-adds.
 *
 * Clearing semantics mirror the backend: a missing field is "no change";
 * to wipe an existing price / duration / description send the matching
 * {@code clearPrice} / {@code clearDuration} / {@code clearDescription}
 * flag instead of null.
 */
export interface UpdateProviderServicePayload {
  customName?: string;
  customPriceCents?: number;
  currency?: string;
  priceType?: 'FIXED' | 'STARTING_FROM';
  durationMinutes?: number;
  description?: string;
  clearPrice?: boolean;
  clearDuration?: boolean;
  clearDescription?: boolean;
}

/**
 * Result of {@link providersService.uploadPortfolioImage}. {@code url}
 * mirrors {@code mediumUrl} — the canonical variant to persist as the
 * portfolio image's imageUrl.
 */
export interface UploadedImage {
  url: string;
  thumbUrl: string;
  mediumUrl: string;
  largeUrl: string;
  /** The random-UUID folder the variants live in. */
  folder: string;
  sizeBytes: number;
}

export interface AddPortfolioImagePayload {
  imageUrl: string;
  caption?: string;
  displayOrder?: number;
  priceCents?: number;
  currency?: string;
}

/** SUPER_ADMIN-only partial update of a portfolio image. */
export interface UpdatePortfolioImagePayload {
  imageUrl?: string;
  caption?: string;
  displayOrder?: number;
  priceCents?: number;
  currency?: string;
  clearCaption?: boolean;
  clearPrice?: boolean;
}

export const providersService = {
  // Public
  search: (params: ProviderSearchQuery = {}): Promise<ApiResponse<ProviderSummary[]>> =>
    ApiClient.get<ProviderSummary[], ProviderSearchQuery>('/providers', params),

  /**
   * Cities where {@code categoryId} already has approved providers,
   * optionally scoped to a country. Drives the category landing page's
   * "available cities" grid — ordered by provider count, busiest first.
   */
  citiesForCategory: async (
    categoryId: string,
    countryCode?: string,
  ): Promise<CategoryCityCoverage[]> =>
    (
      await ApiClient.get<
        CategoryCityCoverage[],
        { categoryId: string; countryCode?: string }
      >('/providers/cities', countryCode ? { categoryId, countryCode } : { categoryId })
    ).data,

  get: async (id: string): Promise<ProviderDetail> =>
    (await ApiClient.get<ProviderDetail>(`/providers/${id}`)).data,

  listServices: async (providerId: string): Promise<ProviderServiceItem[]> =>
    (await ApiClient.get<ProviderServiceItem[]>(`/providers/${providerId}/services`)).data,

  // Self-service (/me).  Resolves to `null` when the user hasn't created
  // a provider profile yet.
  //
  // Two backend behaviours we tolerate transparently:
  //   • Newer build → HTTP 200 with `data: null` (preferred shape).
  //   • Older build → HTTP 404 with NOT_FOUND. We swallow that single
  //     case and also return null, so consumers (BecomeProviderPage,
  //     DashboardPage, AccountSettingsPage) all see "no profile yet"
  //     as a regular branch instead of an error toast. Any other
  //     failure (network, 401, 5xx) is re-thrown so genuine problems
  //     still surface.
  getMine: async (): Promise<ProviderMine | null> => {
    try {
      return (await ApiClient.get<ProviderMine | null>('/providers/me')).data;
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 404) {
        return null;
      }
      throw err;
    }
  },

  become: async (payload: BecomeProviderPayload): Promise<ProviderMine> =>
    (await ApiClient.post<ProviderMine>('/providers/me', payload)).data,

  updateMine: async (payload: UpdateProviderPayload): Promise<ProviderMine> =>
    (await ApiClient.put<ProviderMine>('/providers/me', payload)).data,

  listMyServices: async (): Promise<ProviderServiceItem[]> =>
    (await ApiClient.get<ProviderServiceItem[]>('/providers/me/services')).data,

  addService: async (payload: AddProviderServicePayload): Promise<ProviderServiceItem> =>
    (await ApiClient.post<ProviderServiceItem>('/providers/me/services', payload)).data,

  updateService: async (
    providerServiceId: string,
    payload: UpdateProviderServicePayload,
  ): Promise<ProviderServiceItem> =>
    (await ApiClient.patch<ProviderServiceItem>(
      `/providers/me/services/${providerServiceId}`,
      payload,
    )).data,

  removeService: async (providerServiceId: string): Promise<void> => {
    await ApiClient.delete(`/providers/me/services/${providerServiceId}`);
  },

  addPortfolioImage: async (payload: AddPortfolioImagePayload): Promise<PortfolioImage> =>
    (await ApiClient.post<PortfolioImage>('/providers/me/portfolio', payload)).data,

  /**
   * Bulk-add multiple portfolio images in a single call. Atomic on the server:
   * if the batch would push the provider above {@link MAX_PORTFOLIO_IMAGES},
   * the whole call is rejected and no image is saved.
   */
  addPortfolioImagesBulk: async (
    items: AddPortfolioImagePayload[],
  ): Promise<PortfolioImage[]> =>
    (
      await ApiClient.post<PortfolioImage[]>('/providers/me/portfolio/bulk', {
        items,
      })
    ).data,

  /**
   * Upload an image file (jpg/png/webp/gif, ≤10 MB). The backend re-encodes
   * it into responsive WebP variants and returns their URLs. Pass `url`
   * (the canonical medium variant) into {@link addPortfolioImage} as
   * `imageUrl`; the thumb/large siblings are derived from it on display
   * (see {@code shared/utils/images.ts}).
   */
  uploadPortfolioImage: async (
    file: File,
    onProgress?: (percent: number) => void,
  ): Promise<UploadedImage> => {
    const form = new FormData();
    form.append('file', file);
    return (await ApiClient.postMultipart<UploadedImage>(
      '/uploads/portfolio',
      form,
      onProgress,
    )).data;
  },

  removePortfolioImage: async (imageId: string): Promise<void> => {
    await ApiClient.delete(`/providers/me/portfolio/${imageId}`);
  },

  /**
   * Replace the current provider's event specializations. An empty array
   * clears the selection. Specializations are provider-controlled — the
   * platform never auto-fills them.
   */
  updateMySpecializations: async (specializationIds: string[]): Promise<ProviderMine> =>
    (await ApiClient.put<ProviderMine>('/providers/me/specializations', {
      specializationIds,
    })).data,

  /**
   * Submit a DRAFT profile for moderation (DRAFT → PENDING).  Idempotent
   * on PENDING/APPROVED.  Throws BUSINESS_RULE_VIOLATION if the profile
   * is below the publish threshold (currently 60 % completeness).
   */
  publishMine: async (): Promise<ProviderMine> =>
    (await ApiClient.post<ProviderMine>('/providers/me/publish')).data,

  /** Profile-completeness snapshot for the wizard ring + dashboard card. */
  myCompleteness: async (): Promise<Completeness> =>
    (await ApiClient.get<Completeness>('/providers/me/completeness')).data,

  // ── Weekly opening hours ────────────────────────────────────
  // Backend stores one row per (provider, day_of_week 0..6). On the wire
  // we exchange `{ days: WeeklyHoursDay[] }`; updates are full-replace —
  // the request must list all 7 days exactly once.

  getProviderHours: async (providerId: string): Promise<WeeklyHours> =>
    (await ApiClient.get<WeeklyHours>(`/providers/${providerId}/hours`)).data,

  getMyHours: async (): Promise<WeeklyHours> =>
    (await ApiClient.get<WeeklyHours>('/providers/me/hours')).data,

  updateMyHours: async (days: WeeklyHoursDay[]): Promise<WeeklyHours> =>
    (await ApiClient.put<WeeklyHours>('/providers/me/hours', { days })).data,

  // Admin moderation
  adminList: (status: ProviderStatus = 'PENDING', page = 0, size = 20):
    Promise<ApiResponse<ProviderSummary[]>> =>
      ApiClient.get<ProviderSummary[], { status: ProviderStatus; page: number; size: number }>(
        '/admin/providers', { status, page, size }),

  adminApprove: async (id: string): Promise<ProviderMine> =>
    (await ApiClient.post<ProviderMine>(`/admin/providers/${id}/approve`)).data,

  adminReject: async (id: string, reason: string): Promise<ProviderMine> =>
    (await ApiClient.post<ProviderMine>(`/admin/providers/${id}/reject`, { reason })).data,

  /** Soft-delete a provider account (SUPER_ADMIN only on the backend). */
  /**
   * Step 1 — ask the backend to email a 6-digit confirmation code to the
   * SUPER_ADMIN's mailbox. Must be called before {@link adminDelete}.
   */
  adminRequestDelete: async (id: string): Promise<void> => {
    await ApiClient.post<void>(`/admin/providers/${id}/delete-request`);
  },

  /**
   * Step 2 — perform the actual soft-delete, presenting the 6-digit code
   * received by email. The token is single-use and expires after 10 min.
   */
  adminDelete: async (id: string, code: string): Promise<void> => {
    await ApiClient.delete(`/admin/providers/${id}`, { code });
  },

  /**
   * Admin variant of {@link get} — returns the provider details for ANY status
   * (PENDING / APPROVED / REJECTED). Use this on admin review/moderation screens;
   * the public {@link get} returns 404 for non-approved profiles.
   */
  adminGet: async (id: string): Promise<ProviderDetail> =>
    (await ApiClient.get<ProviderDetail>(`/admin/providers/${id}`)).data,

  /** Admin / SUPER_ADMIN : add one image to any provider's portfolio. */
  adminAddPortfolioImage: async (
    providerId: string,
    payload: AddPortfolioImagePayload,
  ): Promise<PortfolioImage> =>
    (
      await ApiClient.post<PortfolioImage>(
        `/admin/providers/${providerId}/portfolio`,
        payload,
      )
    ).data,

  /** Admin / SUPER_ADMIN : bulk add images, atomic 20-cap enforcement. */
  adminAddPortfolioImagesBulk: async (
    providerId: string,
    items: AddPortfolioImagePayload[],
  ): Promise<PortfolioImage[]> =>
    (
      await ApiClient.post<PortfolioImage[]>(
        `/admin/providers/${providerId}/portfolio/bulk`,
        { items },
      )
    ).data,

  /** SUPER_ADMIN only : partial update of any portfolio image. */
  adminUpdatePortfolioImage: async (
    providerId: string,
    imageId: string,
    payload: UpdatePortfolioImagePayload,
  ): Promise<PortfolioImage> =>
    (
      await ApiClient.patch<PortfolioImage>(
        `/admin/providers/${providerId}/portfolio/${imageId}`,
        payload,
      )
    ).data,

  /** Admin / SUPER_ADMIN : remove any portfolio image (moderation). */
  adminRemovePortfolioImage: async (
    providerId: string,
    imageId: string,
  ): Promise<void> => {
    await ApiClient.delete(
      `/admin/providers/${providerId}/portfolio/${imageId}`,
    );
  },
};
